import { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, del, get, patch, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad, useScanner } from '../lib/hooks';
import { useSession } from '../lib/session';
import { usePlan } from '../lib/plan';
import { INDUSTRIES, isIndustryId } from '../../shared/industries';
import { calcLines, cartToRequest, checkoutRequestId, faceValueCents, useCart, type Cart, type CartLine } from '../lib/cart';
import { DeviceArt, ProductPicture, TASK_ART } from '../ui/art';
import { DevicePhoto } from '../ui/photo';
import { calculateCart, formatCents, formatRatePpm } from '../../shared/money';
import { formatDateTime, formatPhone, normalizePhone, zone } from '../../shared/format';
import { cleanMemberCode } from '../../shared/member';
import { parseTicketNumber } from '../../shared/repairs';
import { Badge, Button, Confirm, Empty, Field, IconButton, Money, MoneyInput, Sheet, Spinner, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { CustomerPicker, CustomerSearch } from './CustomerPicker';
import { OpenDrawerSheet } from './Drawer';
import { SaleMenu } from './SaleMenu';

/** menu: Apple, Samsung, Google, Moto, WiFi, Accessories. phones: phones in stock by IMEI. products: everything by category. */
type Mode = 'menu' | 'phones' | 'products';
const PRODUCT_CATS = [
  { id: '', label: 'All' },
  { id: 'cases', label: 'Cases' },
  { id: 'screen_protectors', label: 'Screen protectors' },
  { id: 'chargers', label: 'Chargers' },
  { id: 'cables', label: 'Cables' },
  { id: 'car_accessories', label: 'Car' },
  { id: 'power_banks', label: 'Power banks' },
  { id: 'headphones', label: 'Headphones' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'adapters', label: 'Adapters' },
  { id: 'sunglasses', label: 'Sunglasses' },
  { id: 'apple', label: 'Apple' },
  { id: 'samsung', label: 'Samsung' },
  { id: 'google', label: 'Google' },
  { id: 'motorola', label: 'Moto' },
  { id: 'wifi', label: 'WiFi' },
  { id: 'perfumes', label: 'Perfume' },
  { id: 'parts', label: 'Repair parts' },
];
/**
 * What a shop that isn't a phone shop sells from: its own categories (the industry's starting
 * ones), and the items it sells as single units (a laptop or a vacuum with a serial number). The
 * phone sale menu (Apple, Samsung, WiFi…) and phones in stock are a phone shop's.
 */
function shopSaleSetup(industry: string) {
  const ind = INDUSTRIES[isIndustryId(industry) ? industry : 'cellphone'];
  const cats = [{ id: '', label: 'All' }, ...ind.categories.filter((c) => c.tracking === 'quantity').map((c) => ({ id: c.id, label: c.name }))];
  if (industry === 'computer') cats.push({ id: 'chargers', label: 'Chargers' }, { id: 'accessories', label: 'Accessories' });
  if (industry === 'vacuum') cats.push({ id: 'accessories', label: 'Accessories' });
  const units = industry === 'computer' ? 'Computers in stock' : industry === 'vacuum' ? 'Vacuums in stock' : null;
  return { cats, units };
}
const FILTERS: { key: string; label: string }[] = [
  { key: 'brand', label: 'Brand' },
  { key: 'model', label: 'Model' },
  { key: 'storage', label: 'Storage' },
  { key: 'color', label: 'Color' },
  { key: 'carrier', label: 'Carrier' },
  { key: 'grade', label: 'Grade' },
];
/** A phone, tablet or laptop on the sale: from inventory, or sold from the brand menus (Apple, Samsung, Google, Moto). */
const DEVICE_CATEGORY = /^(phones|tablets|ipads|macbooks|consoles|apple|samsung|google|motorola)(_|$)/;
const sellsADevice = (lines: CartLine[]) =>
  lines.some((l) => l.kind === 'device' || (l.kind === 'product' && l.art?.kind === 'product' && !!l.art.categoryId && DEVICE_CATEGORY.test(l.art.categoryId)));
/** A phone bought from a customer is held for a while before it's sold again (Settings → Buy & trade-in). */
const onHold = (d: { hold_until?: number | null }) => !!d.hold_until && d.hold_until > Date.now();
const holdDay = (ms: number) => new Intl.DateTimeFormat('en-US', { timeZone: zone.current, month: 'short', day: 'numeric' }).format(new Date(ms));

export function SaleScreen() {
  const { me } = useSession();
  const toast = useToast();
  const cart = useCart();
  // A phone shop sells from the phone menu; computer, auto and vacuum shops from their own items.
  const plan = usePlan();
  const phoneShop = plan.industry === 'cellphone';
  const shop = shopSaleSetup(plan.industry);
  const [mode, setMode] = useState<Mode>(phoneShop ? 'menu' : 'products');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 200);
  const [cat, setCat] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [pickFilter, setPickFilter] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [pickCustomer, setPickCustomer] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // The cart line that was just added flashes and scrolls into view (instead of a pop-up message).
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const flashLine = (key: string | null) => { if (key) setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 })); };
  const linesRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = linesRef.current;
    const el = flashRef.current;
    if (!box || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight;
  }, [flash]);
  const drawer = useLoad(() => (me?.register ? get('/api/drawer') : Promise.resolve(null)), [me?.register?.id]);
  const drawerOpen = !!drawer.data?.shift;

  const facets = useLoad(() => get('/api/devices/facets'), []);
  const devices = useLoad(
    () => (mode === 'phones' ? get(`/api/devices${qs({ q: dq, ...filters, limit: 60 })}`) : Promise.resolve([])),
    [mode, dq, JSON.stringify(filters)],
  );
  const products = useLoad(
    () => (mode === 'products' ? get(`/api/products${qs({ q: dq, category: cat, limit: 80 })}`) : Promise.resolve([])),
    [mode, dq, cat],
  );

  // What's scanned or typed in the box goes in the cart (the same as the cart opened from the top bar).
  const { addByCode: addCode, addDevice, holdPrompt } = useCartAdd(flashLine);
  const addByCode = async (code: string) => {
    if ((await addCode(code)) === 'added') setQ('');
  };

  // Not while paying: a scan then would change the cart behind the Payment window.
  useScanner((code) => addByCode(code), !paying);

  // Parked sales at this store (any tablet here can bring one back). Checked now and then: another tablet may park one.
  const parked = useLoad<ParkedSale[]>(() => (me?.register ? get('/api/parked-sales') : Promise.resolve([])), [me?.register?.id]);
  const reloadParked = parked.reload;
  useEffect(() => {
    const t = setInterval(reloadParked, 30_000);
    return () => clearInterval(t);
  }, [reloadParked]);
  const [showParked, setShowParked] = useState(false);
  // A custom item (one-off, not in inventory), with what was searched for as its name.
  const [customFor, setCustomFor] = useState<string | null>(null);

  if (!me?.register) {
    return (
      <div className="page">
        <div className="page-head">
          <h1>New sale</h1>
          <Button icon="history" onClick={() => navigate('/sales')}>Sales history</Button>
        </div>
        <div className="banner warn"><Icon.alert /><div>Selling needs a store tablet. Open STORE OS on the store's tablet, or make this device a store tablet from the home screen.</div></div>
      </div>
    );
  }

  return (
    <div className="sale">
      <section className="sale-left" aria-label="Find items">
        <div className="spread">
          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <h1 style={{ whiteSpace: 'nowrap' }}>New sale</h1>
            <IconButton icon="history" label="Sales history" onClick={() => navigate('/sales')} />
          </div>
          <div className="tabs" role="tablist" aria-label="What to sell">
            {phoneShop ? <button type="button" role="tab" className="tab" aria-selected={mode === 'menu'} onClick={() => setMode('menu')}>Menu</button> : null}
            {phoneShop || shop.units ? <button type="button" role="tab" className="tab" aria-selected={mode === 'phones'} onClick={() => setMode('phones')}>{phoneShop ? 'Phones in stock' : shop.units}</button> : null}
            <button type="button" role="tab" className="tab" aria-selected={mode === 'products'} onClick={() => setMode('products')}>{phoneShop ? 'All items' : 'Parts and products'}</button>
          </div>
        </div>
        {!drawerOpen && !drawer.loading ? (
          <div className="banner warn">
            <Icon.cash />
            <div className="grow"><div className="strong">The cash drawer is closed.</div><div className="small">Count the drawer and enter the starting cash before the first sale.</div></div>
            <OpenDrawerButton onOpened={drawer.reload} />
          </div>
        ) : null}
        {parked.data?.length ? (
          <div className="banner">
            <Icon.clock />
            <div className="grow small"><span className="strong">{parked.data.length} parked sale{parked.data.length === 1 ? '' : 's'}</span> at this store, waiting to be brought back.</div>
            <Button size="sm" onClick={() => { reloadParked(); setShowParked(true); }}>Parked ({parked.data.length})</Button>
          </div>
        ) : null}
        <form className="search" onSubmit={(e) => { e.preventDefault(); addByCode(q); }}>
          <Icon.scan />
          <input
            ref={inputRef}
            className="input input-lg"
            placeholder={mode === 'phones' ? (phoneShop ? 'Scan an IMEI or member card, or type a model' : 'Scan a serial number or member card, or type a model') : mode === 'menu' ? 'Scan, or search the menu: iPhone 15, S24, case, charger…' : 'Scan a barcode or member card, or type a name'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Scan or search"
            autoFocus
          />
        </form>
        {mode === 'menu' ? (
          <SaleMenu
            q={q}
            onAddProduct={(p, opts) => flashLine(cart.addProduct(p, 1, opts))}
            onAddDevice={addDevice}
            onCustomItem={(name) => setCustomFor(name)}
            inCart={(id) => cart.cart.lines.some((l) => l.id === id)}
          />
        ) : mode === 'phones' ? (
          <>
            <div className="chips" aria-label={phoneShop ? 'Phone filters' : 'Filters'}>
              {FILTERS.filter((f) => phoneShop || (f.key !== 'carrier' && f.key !== 'storage')).map((f) => (
                <button key={f.key} type="button" className="chip" aria-pressed={!!filters[f.key]} onClick={() => (filters[f.key] ? setFilters(dropKey(filters, f.key)) : setPickFilter(f.key))}>
                  {filters[f.key] ? <>{f.key === 'grade' ? gradeLabel(filters[f.key]) : filters[f.key]} <span className="x" aria-hidden>×</span></> : <>{f.label} <Icon.chevronRight /></>}
                </button>
              ))}
              {Object.keys(filters).length ? <button type="button" className="link-btn" onClick={() => setFilters({})}>Clear filters</button> : null}
            </div>
            {devices.loading && !devices.data ? <Spinner /> : null}
            <div className="items">
              {(devices.data ?? []).map((d: any) => (
                <button key={d.id} type="button" className="item" onClick={() => addDevice(d)} disabled={cart.cart.lines.some((l) => l.id === d.id)}>
                  <span className="pic-wrap"><DevicePhoto url={d.image_url} brand={d.brand} model={d.model} color={d.color} category={d.category_id} size={92} /></span>
                  <span className="t">{d.title}</span>
                  <span className="s">{d.detail}</span>
                  {d.battery_health ? <span className="s">Battery {d.battery_health}%</span> : null}
                  {onHold(d) ? <span><Badge tone="amber" icon="clock">On hold until {holdDay(d.hold_until)}</Badge></span> : null}
                  <span className="p"><Money cents={d.price_cents} />{cart.cart.lines.some((l) => l.id === d.id) ? <Badge tone="blue" icon="check">In cart</Badge> : null}</span>
                </button>
              ))}
            </div>
            {devices.data && !devices.data.length ? <Empty icon="search" title={phoneShop ? 'No phones in stock match.' : 'Nothing in stock matches.'}>Try fewer filters, or check another store in Inventory.</Empty> : null}
          </>
        ) : (
          <>
            <div className="chips" aria-label="Categories">
              {(phoneShop ? PRODUCT_CATS : shop.cats).map((c) => (
                <button key={c.id} type="button" className="chip" aria-pressed={cat === c.id} onClick={() => setCat(c.id)}>{c.label}</button>
              ))}
            </div>
            {products.loading && !products.data ? <Spinner /> : null}
            <div className="items">
              {(products.data ?? []).map((p: any) => (
                <button key={p.id} type="button" className="item" onClick={() => flashLine(cart.addProduct(p))}>
                  <span className="pic-wrap"><ProductPicture url={p.image_url} categoryId={p.category_id} name={p.name} size={92} /></span>
                  <span className="t">{p.name}</span>
                  <span className="s">{[p.brand, p.attrs?.size].filter(Boolean).join(' · ') || p.category_name}</span>
                  <span className="s">{p.qty ?? 0} in stock{p.low ? ' · LOW' : ''}</span>
                  <span className="p"><Money cents={p.price_cents} />{p.low ? <Badge tone="amber" icon="alert">Low</Badge> : null}</span>
                </button>
              ))}
            </div>
            {products.data && !products.data.length ? <Empty icon="search" title="No products match." /> : null}
          </>
        )}
      </section>

      <aside className="cart" aria-label="Cart">
        <CartPanel drawerOpen={drawerOpen} onPay={() => setPaying(true)} flash={flash} linesRef={linesRef} flashRef={flashRef} onAdded={flashLine} onParked={reloadParked} />
      </aside>

      {pickFilter ? (
        <FilterSheet
          field={pickFilter}
          facets={facets.data}
          brand={filters.brand}
          onClose={() => setPickFilter(null)}
          onPick={(v) => { setFilters({ ...filters, [pickFilter]: v }); setPickFilter(null); }}
        />
      ) : null}
      {pickCustomer ? <CustomerPicker onClose={() => setPickCustomer(false)} onPick={(c) => { cart.setCustomer(c); setPickCustomer(false); }} /> : null}
      {paying ? <PaySheet onClose={() => setPaying(false)} /> : null}
      {holdPrompt}
      {showParked ? <ParkedSheet list={parked.data ?? []} loading={parked.loading} onChanged={reloadParked} onClose={() => setShowParked(false)} /> : null}
      {customFor !== null ? <CustomItemSheet name={customFor} onClose={() => setCustomFor(null)} onAdd={(c) => { flashLine(cart.addCustom(c)); setCustomFor(null); setQ(''); }} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- the cart (New sale, and the Cart button on every screen)

/**
 * What a scan (or a code typed in the sale's box) puts in the cart: a member card or phone number
 * (the customer), a repair ticket, a product's barcode or SKU, a phone's IMEI. "added": it's in
 * (the box can be cleared); "shown": a message said why not; "none": nothing has that code.
 * `holdPrompt` is the "Sell it now" question for a phone still on its buy hold: render it.
 */
export function useCartAdd(onAdded: (key: string | null) => void) {
  const { me, can } = useSession();
  const toast = useToast();
  const cart = useCart();
  const { run, busy } = useAction();
  // A phone still on its buy hold, for someone who can end the hold.
  const [held, setHeld] = useState<any>(null);

  const put = (d: any): boolean => {
    const key = cart.addDevice(d);
    if (!key) { toast('That phone is already in the cart.'); return false; }
    onAdded(key);
    return true;
  };

  /** True: it's in the cart. "held": the Sell it now question is up. False: a message said why not. */
  const addDevice = (d: any): boolean | 'held' => {
    if (d.location_id !== me?.location?.id) { toast(`${d.title} is at ${d.location_name}. Transfer it here first.`, 'error'); return false; }
    if (d.status !== 'in_stock') { toast(`${d.title} isn't ready to sell (${d.status.replace('_', ' ')}).`, 'error'); return false; }
    // Caught here, not at Pay: a phone bought from a customer waits out its hold first.
    if (onHold(d)) {
      if (can('inventory.adjust')) { setHeld(d); return 'held'; }
      toast(`${d.title} is on hold until ${formatDateTime(d.hold_until)}. A manager can end the hold.`, 'error');
      return false;
    }
    return put(d);
  };

  const sellNow = async () => {
    const d = held;
    const freed = await run(() => patch(`/api/devices/${d.id}`, { holdUntil: null }));
    setHeld(null);
    if (freed) put({ ...d, ...freed });
  };

  const holdPrompt = held
    ? createPortal(
      <Sheet title="On hold" onClose={() => setHeld(null)} footer={<><Button onClick={() => setHeld(null)}>Not now</Button><Button variant="primary" icon="bag" busy={busy} onClick={sellNow}>Sell it now</Button></>}>
        <p style={{ margin: 0 }}><strong>{held.title}</strong> is on hold until {formatDateTime(held.hold_until)}: phones bought from customers wait before they’re sold again.</p>
        <p className="small muted" style={{ margin: 0 }}>Sell it now ends the hold and puts it in the cart. It’s noted in the phone’s history.</p>
      </Sheet>,
      document.body,
    )
    : null;

  /** A scanned member card (QR) or a typed phone number puts the customer on the sale. */
  const attachMember = async (raw: string): Promise<'added' | 'shown' | 'none'> => {
    const code = cleanMemberCode(raw);
    const phone = !code && /^[\d\s().+-]+$/.test(raw) ? normalizePhone(raw) : null;
    if (!code && !phone) return 'none';
    try {
      const { result } = await get(`/api/member-lookup${qs({ code: code ?? phone })}`);
      if (!result) return 'none'; // nobody has that number: it may be a product code
      cart.setCustomer({ id: result.id, name: result.name, phone: result.phone, email: result.email, store_credit_cents: result.store_credit_cents, points_balance: result.points_balance, member_code: result.member_code });
      toast(`${result.name} added · ${result.points_balance.toLocaleString('en-US')} points`, 'good');
      return 'added';
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not find that member.', 'error');
      return 'shown';
    }
  };

  /** A scanned repair ticket (T-1001) goes into the sale with its customer. */
  const addTicket = async (code: string): Promise<'added' | 'shown' | 'none'> => {
    try {
      const { repairs } = await get(`/api/repairs${qs({ q: code, status: 'all', limit: 1 })}`);
      if (!repairs?.length) return 'none';
      const r = await get(`/api/repairs/${repairs[0].id}`);
      if (r.paid) {
        toast(`${r.ticket} is already paid (receipt #${r.paid.receipt_no}).`, 'info');
        return 'shown';
      }
      const key = cart.addRepair(r);
      if (!key) toast(`${r.ticket} is already in the sale.`, 'info');
      else onAdded(key);
      if (!cart.cart.customer && r.customer) {
        const c = await get(`/api/customers/${r.customer.id}`).catch(() => null);
        if (c) cart.setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
      }
      return 'added';
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not open that ticket.', 'error');
      return 'shown';
    }
  };

  const addByCode = async (code: string): Promise<'added' | 'shown' | 'none'> => {
    const c = code.trim();
    if (!c) return 'none';
    const member = await attachMember(c);
    if (member !== 'none') return member;
    if (parseTicketNumber(c) !== null) {
      const ticket = await addTicket(c);
      if (ticket !== 'none') return ticket;
    }
    try {
      const { result } = await get(`/api/lookup${qs({ code: c })}`);
      if (!result) {
        toast(`Nothing matches "${c}". Try searching by name.`, 'error');
        return 'shown';
      }
      if (result.type === 'product') {
        onAdded(cart.addProduct(result.product));
        return 'added';
      }
      // On hold: the Sell it now question has it now.
      return addDevice(result.device) === false ? 'shown' : 'added';
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Lookup failed.', 'error');
      return 'shown';
    }
  };

  return { addByCode, addDevice, holdPrompt };
}

/** Totals the way the server charges them: repairs as their taxed parts and tax-free labor, no tax when it was removed. */
export function useCartTotals() {
  const { me } = useSession();
  const { cart } = useCart();
  const rate = me?.location?.tax_rate_ppm ?? 0;
  const pieces = useMemo(() => calcLines(cart.lines), [cart.lines]);
  const items = useMemo(() => calculateCart(pieces, cart.cartDiscountCents, cart.taxRemoved ? 0 : rate), [pieces, cart.cartDiscountCents, cart.taxRemoved, rate]);
  // Deposits are at face value: one being taken adds to the total; ones paid on a repair here come off it.
  const taken = cart.lines.reduce((s, l) => s + (l.kind === 'deposit' ? l.unitPriceCents : 0), 0);
  const credits = useMemo(() => cart.lines.flatMap((l) => (l.kind === 'repair' ? (l.deposits ?? []).map((d) => ({ ...d, ticket: l.name })) : [])), [cart.lines]);
  const totals = useMemo(() => ({ ...items, subtotal: items.subtotal + taken, total: items.total + faceValueCents(cart.lines) }), [items, taken, cart.lines]);
  // The tax "Remove taxes" takes off (or would).
  const fullTax = useMemo(() => (cart.taxRemoved ? calculateCart(pieces, cart.cartDiscountCents, rate).tax : items.tax), [pieces, cart.cartDiscountCents, cart.taxRemoved, rate, items.tax]);
  /** itemsSubtotal: what a discount can come off (not deposits). */
  return { rate, pieces, totals, fullTax, itemsSubtotal: items.subtotal, credits };
}

/**
 * The cart: the customer, what's in it, the discount, the tax (and Remove taxes), the total,
 * trade-ins, and Pay. Beside the menu on New sale; on any other screen it opens from the Cart
 * button in the top bar.
 */
export function CartPanel({ drawerOpen, onPay, flash, linesRef, flashRef, emptyHint = 'Scan an item or tap one on the left.', onAdded, onParked }: {
  drawerOpen: boolean;
  onPay: () => void;
  /** The line just added flashes (New sale). */
  flash?: { key: string; n: number } | null;
  linesRef?: Ref<HTMLDivElement>;
  flashRef?: Ref<HTMLDivElement>;
  emptyHint?: string;
  /** A line was added here (a custom item): New sale flashes it. */
  onAdded?: (key: string) => void;
  /** The sale was parked: New sale refreshes its Parked list. */
  onParked?: () => void;
}) {
  const cart = useCart();
  const { me } = useSession();
  const [editLine, setEditLine] = useState<CartLine | null>(null);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [parking, setParking] = useState(false);
  const { rate, pieces, totals, fullTax, itemsSubtotal, credits } = useCartTotals();
  const removed = cart.cart.taxRemoved ?? null;
  const count = cart.cart.lines.length;
  // Bill payments go to the carrier in full, so a whole-sale discount can't stay on with one: it
  // can still be taken off (the button stays), and the cart says why Pay waits.
  const billPay = cart.cart.lines.some((l) => l.kind === 'billpay');
  const billPayDiscount = billPay && cart.cart.cartDiscountCents > 0;
  const perLine = useMemo(() => {
    const out = cart.cart.lines.map(() => ({ net: 0, discount: 0 }));
    totals.lines.forEach((t, j) => {
      const o = out[pieces[j].owner];
      o.net += t.net;
      o.discount += t.lineDiscount + t.cartDiscountShare;
    });
    return out;
  }, [totals, pieces, cart.cart.lines]);
  // A trade-in comes off the phone they buy: on a phone sale the customer's store credit is
  // taken off the total (at payment; tax is still on the full price).
  const tradeCredit = sellsADevice(cart.cart.lines) ? Math.max(0, Math.min(cart.cart.customer?.store_credit_cents ?? 0, totals.total)) : 0;
  const due = totals.total - tradeCredit;
  return (
    <>
      <div className="cart-head">
        {cart.cart.customer ? (
          <div className="customer-pill">
            <Icon.user />
            <div className="grow">
              <div className="strong">{cart.cart.customer.name}</div>
              <div className="small muted">{formatPhone(cart.cart.customer.phone) || 'No phone'}{cart.cart.customer.points_balance ? ` · ${cart.cart.customer.points_balance.toLocaleString('en-US')} points` : ''}{cart.cart.customer.store_credit_cents ? ` · ${formatCents(cart.cart.customer.store_credit_cents)} store credit` : ''}</div>
            </div>
            <IconButton icon="close" label="Remove customer" onClick={() => cart.setCustomer(null)} />
          </div>
        ) : (
          <CustomerSearch value={null} onChange={(c) => cart.setCustomer(c)} placeholder="Search customer name or phone" />
        )}
      </div>
      <div className="cart-lines" ref={linesRef}>
        {cart.cart.lines.length === 0 ? <Empty icon="bag" title="The cart is empty.">{emptyHint}</Empty> : null}
        {cart.cart.lines.map((l, i) => {
          const t = perLine[i];
          const lit = flash?.key === l.key;
          return (
            <div key={lit ? `${l.key}#${flash?.n}` : l.key} className={lit ? 'cart-line flash' : 'cart-line'} ref={lit ? flashRef : undefined}>
              <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                <span className="thumb" style={{ width: 44, height: 44 }}>
                  {l.kind === 'billpay' || l.kind === 'activation' || l.kind === 'order' ? TASK_ART[l.kind]({ size: 40 }) : l.art?.kind === 'device' ? <DeviceArt brand={l.art.brand} model={l.art.model} color={l.art.color} category={l.art.categoryId} size={44} /> : <ProductPicture url={l.image} categoryId={l.art?.kind === 'product' ? l.art.categoryId : null} name={l.name} size={44} />}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="d">{l.name}</div>
                  {l.detail ? <div className="x">{l.detail}</div> : null}
                  {l.kind !== 'custom' && l.unitPriceCents !== l.listPriceCents ? <div className="x">Price changed from {formatCents(l.listPriceCents)}</div> : null}
                  {t && t.discount > 0 ? <div className="x">Discount −{formatCents(t.discount)}</div> : null}
                  {(l.deposits ?? []).map((d) => <div key={d.receiptNo} className="x">Deposit paid: {formatCents(d.amountCents)} (receipt #{d.receiptNo})</div>)}
                </div>
              </div>
              <div className="amt">{formatCents(l.kind === 'deposit' ? l.unitPriceCents : t ? t.net : l.qty * l.unitPriceCents)}</div>
              <div className="ctl">
                {l.kind === 'repair' ? (
                  <Badge tone="blue" icon="wrench">Repair</Badge>
                ) : l.kind === 'billpay' ? (
                  <Badge tone="blue" icon="bill">Bill pay</Badge>
                ) : l.kind === 'activation' ? (
                  <Badge tone="blue" icon="sim">Activation</Badge>
                ) : l.kind === 'order' ? (
                  <Badge tone="blue" icon="truck">Order</Badge>
                ) : l.kind === 'deposit' ? (
                  <Badge tone="blue" icon="cash">Deposit</Badge>
                ) : l.kind === 'product' || l.kind === 'custom' ? (
                  <div className="qty">
                    <button type="button" aria-label={`One less ${l.name}`} onClick={() => cart.setQty(l.key, l.qty - 1)}><Icon.minus /></button>
                    <span aria-label="Quantity">{l.qty}</span>
                    <button type="button" aria-label={`One more ${l.name}`} onClick={() => cart.setQty(l.key, l.qty + 1)}><Icon.plus /></button>
                  </div>
                ) : <Badge>1 phone</Badge>}
                <span className="grow" />
                {l.kind === 'product' || l.kind === 'device' || l.kind === 'custom' ? <Button size="sm" variant="ghost" icon="tag" onClick={() => setEditLine(l)}>Price</Button> : null}
                <IconButton icon="trash" label={`Remove ${l.name}`} onClick={() => cart.remove(l.key)} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="cart-foot">
        <div className="sum-row"><span>Subtotal</span><Money cents={totals.subtotal} /></div>
        <div className="sum-row">
          {billPayDiscount ? (
            <button type="button" className="link-btn" onClick={() => cart.setCartDiscount(0)}>Remove discount</button>
          ) : (
            <button type="button" className="link-btn" onClick={() => setDiscountOpen(true)} disabled={!count || billPay}>{totals.discount ? 'Discount' : 'Add discount'}</button>
          )}
          <span className="num">{totals.discount ? `−${formatCents(totals.discount)}` : ''}</span>
        </div>
        {billPayDiscount ? (
          <div className="banner warn"><Icon.alert /><div className="small">Bill payments go to the carrier in full, so they can’t share a sale with a discount. Remove the discount, or ring the bill payment up on its own.</div></div>
        ) : null}
        <div className="sum-row tax-row">
          <span className="tax-label">
            {removed ? <span>Tax removed <span className="muted">· {removed.reason}</span></span> : <span>Tax ({formatRatePpm(rate)})</span>}
            {removed ? (
              <Button size="sm" variant="ghost" onClick={() => cart.setTaxRemoved(null)}>Put the tax back</Button>
            ) : fullTax > 0 ? (
              <Button size="sm" variant="secondary" onClick={() => setTaxOpen(true)}>Remove taxes</Button>
            ) : null}
          </span>
          <Money cents={totals.tax} />
        </div>
        {credits.map((c) => (
          <div key={`${c.ticket}-${c.receiptNo}`} className="sum-row"><span>Deposit paid on receipt #{c.receiptNo}</span><span className="num">−{formatCents(c.amountCents)}</span></div>
        ))}
        <div className="sum-row total"><span>Total</span><Money cents={totals.total} /></div>
        {totals.total < 0 ? <div className="banner warn"><Icon.alert /><div className="small">The deposit is more than this sale comes to. Check the repair’s price, or refund the deposit first.</div></div> : null}
        {(cart.cart.tradeIns ?? []).map((t) => (
          <div key={t.id} className="trade-row">
            <Icon.swap />
            <span className="grow"><span className="strong">Trade-in {t.number}</span> · {t.device}</span>
            <span className="num">{formatCents(t.credit_cents)}</span>
            <IconButton icon="close" label={`Take trade-in ${t.number} off this sale`} onClick={() => cart.removeTradeIn(t.id)} />
          </div>
        ))}
        {tradeCredit > 0 ? (
          <>
            <div className="sum-row"><span>Trade-in / store credit</span><span className="num">−{formatCents(tradeCredit)}</span></div>
            <div className="sum-row"><span className="strong">Left to pay</span><Money cents={due} className="strong" /></div>
          </>
        ) : null}
        {sellsADevice(cart.cart.lines) ? (
          <Button variant="secondary" icon="swap" block onClick={() => navigate('/trade?for=sale')}>{(cart.cart.tradeIns ?? []).length ? 'Add another trade-in' : 'Trading in a phone? Add a trade-in'}</Button>
        ) : null}
        <Button variant="primary" size="xl" block className="pay-btn" disabled={!count || !drawerOpen || billPayDiscount || due < 0} onClick={onPay}>
          {drawerOpen ? `Pay ${formatCents(Math.max(0, due))}` : 'Open the drawer to sell'}
        </Button>
        <div className="row" style={{ justifyContent: 'center', gap: 2 }}>
          <Button variant="ghost" size="sm" icon="plus" style={{ paddingLeft: 8, paddingRight: 8 }} onClick={() => setCustomOpen(true)}>Custom item</Button>
          {count && me?.register ? <Button variant="ghost" size="sm" style={{ paddingLeft: 8, paddingRight: 8 }} onClick={() => setParking(true)}>Park sale</Button> : null}
          {count ? <Button variant="ghost" size="sm" style={{ paddingLeft: 8, paddingRight: 8 }} onClick={() => setClearing(true)}>Clear sale</Button> : null}
        </div>
      </div>
      {/* On the page itself, not inside the cart (it stays above the top bar and over the cart's own sheet). */}
      {editLine ? createPortal(<LineSheet line={editLine} onClose={() => setEditLine(null)} />, document.body) : null}
      {discountOpen ? createPortal(<DiscountSheet subtotal={itemsSubtotal} current={cart.cart.cartDiscountCents} onClose={() => setDiscountOpen(false)} onSave={(c) => { cart.setCartDiscount(c); setDiscountOpen(false); }} />, document.body) : null}
      {taxOpen ? createPortal(<RemoveTaxSheet tax={fullTax} onClose={() => setTaxOpen(false)} onSave={(reason) => { cart.setTaxRemoved(reason); setTaxOpen(false); }} />, document.body) : null}
      {customOpen ? createPortal(<CustomItemSheet onClose={() => setCustomOpen(false)} onAdd={(c) => { const key = cart.addCustom(c); setCustomOpen(false); onAdded?.(key); }} />, document.body) : null}
      {parking ? createPortal(<ParkSheet onClose={() => setParking(false)} onParked={() => { setParking(false); onParked?.(); }} />, document.body) : null}
      {clearing ? createPortal(
        <Confirm title="Clear this sale?" confirmLabel="Clear sale" danger onClose={() => setClearing(false)} onConfirm={() => { cart.clear(); setClearing(false); }}>
          <p style={{ margin: 0 }}>{clearText(count, cart.cart.customer?.name ?? null, cart.cart.cartDiscountCents > 0)}</p>
          {me?.register ? <p className="small muted" style={{ margin: 0 }}>Coming back for it? Park the sale instead.</p> : null}
        </Confirm>,
        document.body,
      ) : null}
    </>
  );
}

/** "All 3 items, the customer (Maria Lopez) and the discount come out of the cart." */
function clearText(count: number, customer: string | null, discount: boolean): string {
  const what = [count === 1 ? 'the item' : `all ${count} items`, customer ? `the customer (${customer})` : '', discount ? 'the discount' : ''].filter(Boolean);
  const list = what.length > 1 ? `${what.slice(0, -1).join(', ')} and ${what[what.length - 1]}` : what[0];
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} ${what.length === 1 && count === 1 ? 'comes' : 'come'} out of the cart.`;
}

/** The cart from any screen (the Cart button in the top bar): the same cart, and Pay. */
export function CartSheet({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const drawer = useLoad(() => (me?.register ? get('/api/drawer') : Promise.resolve(null)), [me?.register?.id]);
  const drawerOpen = !!drawer.data?.shift;
  const [paying, setPaying] = useState(false);
  // Scanned with the cart open: it goes in the cart (not while paying).
  const { addByCode, holdPrompt } = useCartAdd(() => undefined);
  useScanner((code) => { addByCode(code); }, !paying);
  // Pay takes the place of the cart; Cancel there comes back here, a finished sale closes both.
  if (paying) return <PaySheet onClose={() => setPaying(false)} onDone={onClose} />;
  const newSale = () => { onClose(); navigate('/sale'); };
  return (
    <Sheet title="Cart" wide focusFirst={false} onClose={onClose} footer={<><Button onClick={onClose}>Close</Button><Button icon="bag" onClick={newSale}>Add items on New sale</Button></>}>
      {holdPrompt}
      {!me?.register ? (
        <div className="banner warn"><Icon.alert /><div>Selling needs a store tablet. Open STORE OS on the store's tablet to take payment.</div></div>
      ) : !drawerOpen && !drawer.loading ? (
        <div className="banner warn">
          <Icon.cash />
          <div className="grow"><div className="strong">The cash drawer is closed.</div><div className="small">Count the drawer and enter the starting cash before the first sale.</div></div>
          <OpenDrawerButton onOpened={drawer.reload} />
        </div>
      ) : null}
      <div className="cart cart-sheet" aria-label="Cart">
        <CartPanel drawerOpen={drawerOpen && !!me?.register} onPay={() => setPaying(true)} emptyHint="Scan an item, or add items on New sale." />
      </div>
    </Sheet>
  );
}

/** Why the tax comes off a sale: the usual reasons, or typed. */
const TAX_REASONS = ['Resale', 'Shipped out of state', 'Tax-exempt organization'];

/** "Remove taxes": no sales tax on this sale, and why. Managers can; anyone else needs a manager's PIN at checkout. */
function RemoveTaxSheet({ tax, onSave, onClose }: { tax: number; onSave: (reason: string) => void; onClose: () => void }) {
  const { can } = useSession();
  const [pick, setPick] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const reason = pick === 'Other' ? other.trim() : pick ?? '';
  return (
    <Sheet title="Remove taxes" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={reason.length < 2} onClick={() => onSave(reason)}>Remove taxes</Button></>}>
      <p style={{ margin: 0 }}>No sales tax on this sale: <strong>{formatCents(tax)}</strong> comes off the total. Why?</p>
      <div className="chips" role="group" aria-label="Why the tax comes off">
        {[...TAX_REASONS, 'Other'].map((r) => <button key={r} type="button" className="chip" aria-pressed={pick === r} onClick={() => setPick(r)}>{r}</button>)}
      </div>
      {pick === 'Other' ? (
        <Field label="Why">
          <input className="input" autoFocus value={other} onChange={(e) => setOther(e.target.value)} maxLength={80} placeholder="e.g. Resale certificate on file" />
        </Field>
      ) : null}
      {can('sale.remove_tax') ? (
        <p className="small muted" style={{ margin: 0 }}>The receipt says the tax was removed and why. It shows on the daily close-out and the reports too.</p>
      ) : (
        <div className="banner warn"><Icon.lock /><div className="small">A manager’s PIN is needed when you finish the sale.</div></div>
      )}
    </Sheet>
  );
}

function dropKey(o: Record<string, string>, k: string) {
  const n = { ...o };
  delete n[k];
  if (k === 'brand') delete n.model;
  return n;
}

function gradeLabel(g: string) {
  return g === 'parts' ? 'Parts only' : `Grade ${g}`;
}

function FilterSheet({ field, facets, brand, onPick, onClose }: { field: string; facets: any; brand?: string; onPick: (v: string) => void; onClose: () => void }) {
  let options: { value: string; count: number }[] = [];
  if (facets) {
    if (field === 'model') options = brand ? facets.models[brand] ?? [] : Object.values(facets.models).flat() as any;
    else options = facets[{ brand: 'brands', storage: 'storages', color: 'colors', carrier: 'carriers', grade: 'grades' }[field] as string] ?? [];
  }
  const title = FILTERS.find((f) => f.key === field)?.label ?? field;
  return (
    <Sheet title={title} onClose={onClose}>
      {field === 'model' && !brand ? <p className="small muted">Tip: pick a brand first to shorten this list.</p> : null}
      {options.length ? (
        <div className="chips">
          {options.map((o) => (
            <button key={o.value} type="button" className="chip" onClick={() => onPick(o.value)} style={{ minHeight: 48, fontSize: 16 }}>
              {field === 'grade' ? gradeLabel(o.value) : o.value} <span className="muted">({o.count})</span>
            </button>
          ))}
        </div>
      ) : (
        <Empty icon="search" title="No options in stock at this store." />
      )}
    </Sheet>
  );
}

function LineSheet({ line, onClose }: { line: CartLine; onClose: () => void }) {
  const cart = useCart();
  const { can } = useSession();
  const [price, setPrice] = useState<number | null>(line.unitPriceCents);
  const [disc, setDisc] = useState<number | null>(line.discountCents || null);
  // A custom item has no regular price: its price is whatever was typed.
  const custom = line.kind === 'custom';
  const save = () => {
    const unit = custom ? price || line.unitPriceCents : price ?? line.listPriceCents;
    cart.updateLine(line.key, { unitPriceCents: unit, ...(custom ? { listPriceCents: unit } : {}), discountCents: disc ?? 0 });
    onClose();
  };
  return (
    <Sheet title={line.name} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" onClick={save}>Save</Button></>}>
      <Field label="Price" hint={custom ? 'custom item' : `Regular price ${formatCents(line.listPriceCents)}`}>
        <MoneyInput value={price} onChange={setPrice} />
      </Field>
      {!can('sale.price_override') && (custom || price !== line.listPriceCents) ? <div className="banner warn"><Icon.lock /><div className="small">A manager will be asked to approve {custom ? 'the custom item' : 'the price change'} at checkout.</div></div> : null}
      <Field label="Discount on this item" hint={line.qty > 1 ? `for all ${line.qty}` : undefined}>
        <MoneyInput value={disc} onChange={setDisc} />
      </Field>
      <div className="row-wrap">
        {[5, 10, 15, 20].map((pct) => (
          <button key={pct} type="button" className="chip" onClick={() => setDisc(Math.round(((price ?? line.listPriceCents) * line.qty * pct) / 100))}>{pct}%</button>
        ))}
        <button type="button" className="chip" onClick={() => setDisc(null)}>No discount</button>
      </div>
    </Sheet>
  );
}

function DiscountSheet({ subtotal, current, onSave, onClose }: { subtotal: number; current: number; onSave: (c: number) => void; onClose: () => void }) {
  const [v, setV] = useState<number | null>(current || null);
  return (
    <Sheet title="Discount on the whole sale" onClose={onClose} footer={<><Button onClick={() => onSave(0)}>Remove discount</Button><Button variant="primary" onClick={() => onSave(v ?? 0)}>Apply</Button></>}>
      <Field label="Amount off"><MoneyInput value={v} onChange={setV} autoFocus /></Field>
      <div className="row-wrap">
        {[5, 10, 15, 20].map((pct) => (
          <button key={pct} type="button" className="chip" onClick={() => setV(Math.round((subtotal * pct) / 100))}>{pct}%</button>
        ))}
      </div>
      <p className="small muted">Discounts over the store limit need a manager PIN.</p>
    </Sheet>
  );
}

interface Pay { method: 'cash' | 'card' | 'store_credit'; amountCents: number; tenderedCents?: number; cardLast4?: string; cardBrand?: string; externalRef?: string }
interface Redeem { points: number; confirm: { last4?: string; name?: boolean } }

/** Use points: pick how many, then check it's really the member before applying. */
function UsePointsPanel({ points, customer, onCancel, onApply }: { points: any; customer: { name: string; phone: string | null }; onCancel: () => void; onApply: (r: Redeem) => Promise<string | null> }) {
  const [n, setN] = useState<string>(String(points.max_redeem_points));
  const [last4, setLast4] = useState('');
  const [nameOk, setNameOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const count = Math.floor(Number(n) || 0);
  const hasPhone = !!customer.phone;
  const ready = count >= points.min_redeem && count <= points.max_redeem_points && (hasPhone ? last4.length === 4 : nameOk);
  const apply = async () => {
    if (hasPhone && customer.phone!.slice(-4) !== last4) return setErr('Those digits don’t match this customer’s phone.');
    setBusy(true);
    setErr(await onApply({ points: count, confirm: hasPhone ? { last4 } : { name: true } }));
    setBusy(false);
  };
  return (
    <div className="card stack">
      <div className="spread"><h3>Use points</h3><Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button></div>
      <p className="small muted">{points.balance.toLocaleString('en-US')} points available. Up to {points.max_redeem_points.toLocaleString('en-US')} can be used on this sale ({formatCents(points.max_redeem_points * points.cents_per_point)} off before tax).</p>
      <Field label="Points to use">
        <input className="input input-lg" inputMode="numeric" value={n} onChange={(e) => setN(e.target.value.replace(/\D/g, ''))} />
      </Field>
      <div className="row-wrap">
        <button type="button" className="chip" onClick={() => setN(String(points.max_redeem_points))}>All {points.max_redeem_points.toLocaleString('en-US')}</button>
        {points.max_redeem_points >= 500 ? <button type="button" className="chip" onClick={() => setN(String(Math.floor(points.max_redeem_points / 2)))}>Half</button> : null}
        <span className="small muted">= {formatCents(count * points.cents_per_point)} off</span>
      </div>
      {hasPhone ? (
        <Field label="Ask for the last 4 digits of their phone" hint="don't read the number out to them">
          <input className="input input-lg" inputMode="numeric" maxLength={4} value={last4} onChange={(e) => { setLast4(e.target.value.replace(/\D/g, '')); setErr(null); }} placeholder="••••" />
        </Field>
      ) : (
        <label className="check"><input type="checkbox" checked={nameOk} onChange={(e) => setNameOk(e.target.checked)} /> I checked their name: {customer.name}</label>
      )}
      {count > points.manager_over ? <div className="banner warn"><Icon.lock /><div className="small">More than {points.manager_over.toLocaleString('en-US')} points needs a manager PIN when you finish.</div></div> : null}
      {err ? <div className="error-text">{err}</div> : null}
      <Button variant="primary" size="lg" block busy={busy} disabled={!ready} onClick={apply}>Use {count.toLocaleString('en-US')} points</Button>
    </div>
  );
}

function PaySheet({ onClose, onDone }: { onClose: () => void; onDone?: () => void }) {
  const cart = useCart();
  const toast = useToast();
  const { run, busy } = useAction();
  // "Drop the payments recorded so far?"
  const [dropAsk, setDropAsk] = useState(false);
  // A try to save the sale didn't come back (it may have gone through).
  const [unsure, setUnsure] = useState(false);
  const [quote, setQuote] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [pays, setPays] = useState<Pay[]>([]);
  const [method, setMethod] = useState<Pay['method'] | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [tendered, setTendered] = useState<number | null>(null);
  const [last4, setLast4] = useState('');
  const [ref, setRef] = useState('');
  // The store's Clover Mini: the amount goes to it and the approval comes back by itself.
  const { me } = useSession();
  const cloverReady = !!me?.clover?.ready;
  // West Coast Wireless runs cards on its Clover; any other shop on its own card reader.
  const reader = usePlan().home ? 'the Clover' : 'your card reader';
  const [cloverWait, setCloverWait] = useState<'sending' | 'checking' | null>(null);
  const [byHand, setByHand] = useState(false);
  // Points used as a discount (before tax), after the cashier checked who the member is.
  const [redeem, setRedeem] = useState<Redeem | null>(null);
  const [usingPoints, setUsingPoints] = useState(false);
  // On a phone sale, the customer's trade-in (store credit) is used first, automatically.
  const [skipCredit, setSkipCredit] = useState(false);
  // The texting box: asked at every checkout with a customer who has a phone (texting runs on
  // West Coast Wireless's own number: its store only, for now).
  const buyer = cart.cart.customer;
  const textsOn = usePlan().home;
  const [texting, setTexting] = useState<{ agreed: boolean; opted_out: boolean; text: string } | null>(null);
  const [smsConsent, setSmsConsent] = useState(false);
  useEffect(() => {
    if (!textsOn || !buyer?.id || !buyer.phone) { setTexting(null); return; }
    let live = true;
    get(`/api/customers/${buyer.id}/texting`).then((t) => { if (live) { setTexting(t); setSmsConsent(!!t.agreed); } }).catch(() => undefined);
    return () => { live = false; };
  }, [textsOn, buyer?.id, buyer?.phone]);

  const priceCart = (r: Redeem | null) => {
    const req = cartToRequest(cart.cart);
    return post('/api/sales/quote', { lines: req.lines, cartDiscountCents: req.cartDiscountCents, customerId: req.customerId, redeemPoints: r?.points || undefined, removeTax: req.removeTax });
  };
  useEffect(() => {
    priceCart(null)
      .then(setQuote)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not price the cart.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPoints = async (r: Redeem): Promise<string | null> => {
    try {
      const q = await priceCart(r);
      if (q.points?.error) return q.points.error;
      setQuote(q);
      setRedeem({ ...r, points: q.points.redeem_points });
      setUsingPoints(false);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Could not use points.';
    }
  };
  const removePoints = async () => {
    try {
      setQuote(await priceCart(null));
      setRedeem(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not update the total.', 'error');
    }
  };

  const total = quote?.total_cents ?? 0;
  const credit = cart.cart.customer?.store_credit_cents ?? 0;
  const hasPhone = sellsADevice(cart.cart.lines);
  const creditUsed = (list: Pay[]) => list.filter((p) => p.method === 'store_credit').reduce((s, p) => s + p.amountCents, 0);
  /** The trade-in credit taken off a phone sale, after the payments already added. */
  const autoCreditFor = (list: Pay[]) =>
    !hasPhone || skipCredit || !quote ? 0 : Math.max(0, Math.min(credit - creditUsed(list), total - list.reduce((s, p) => s + p.amountCents, 0)));
  const autoCredit = autoCreditFor(pays);
  const paid = pays.reduce((s, p) => s + p.amountCents, 0) + autoCredit;
  const remaining = total - paid;
  const creditLeft = credit - autoCredit - creditUsed(pays);

  const choose = (m: Pay['method']) => {
    setMethod(m);
    setAmount(m === 'store_credit' ? Math.min(remaining, creditLeft) : remaining);
    setTendered(null);
    setLast4('');
    setRef('');
  };

  const complete = async (all: Pay[]) => {
    const req = cartToRequest(cart.cart);
    // The same id every time this cart is paid for: if "No connection" hid a sale that went
    // through, trying again gets that sale back instead of ringing it up twice.
    const requestId = checkoutRequestId(cart.checkoutSeed(), req);
    const auto = autoCreditFor(all);
    const payments: Pay[] = auto > 0 ? [{ method: 'store_credit', amountCents: auto }, ...all] : all;
    const tradeInIds = (cart.cart.tradeIns ?? []).map((t) => t.id);
    const consent = texting && !texting.opted_out ? { smsConsent } : {};
    let sale: any;
    try {
      sale = await run((approval?: ApprovalInput) => post('/api/sales', { ...req, payments, approval, requestId, ...consent, ...(redeem ? { redeemPoints: redeem.points, redeemConfirm: redeem.confirm } : {}), ...(tradeInIds.length ? { tradeInIds } : {}) }), { quiet: true });
    } catch (e) {
      // No answer, or the server failed: it may have been saved. Trying again is safe (same id).
      if (isApiError(e) && (e.code === 'offline' || e.status >= 500)) setUnsure(true);
      toast(e instanceof Error ? e.message : 'Something went wrong.', 'error');
      return;
    }
    if (sale) {
      cart.clear();
      if (sale.repeated) toast(`That sale already went through (receipt #${sale.receipt_no}). Nothing was charged twice.`, 'info');
      for (const w of sale.warnings ?? []) toast(w, 'info');
      onDone?.();
      navigate(`/sales/${sale.id}?done=1`);
    }
  };

  /** A card approved on the Clover Mini goes straight on the sale (and finishes it when it covers the rest). */
  const addCloverPayment = (p: { amount_cents: number; card_last4: string | null; card_brand: string | null; external_ref: string }) => {
    const pay: Pay = { method: 'card', amountCents: p.amount_cents, cardLast4: p.card_last4 ?? undefined, cardBrand: p.card_brand ?? undefined, externalRef: p.external_ref };
    const next = [...pays, pay];
    setPays(next);
    setMethod(null);
    toast(`Approved on the Clover${p.card_brand ? `: ${p.card_brand}` : ''}${p.card_last4 ? ` ••••${p.card_last4}` : ''}.`, 'good');
    if (p.amount_cents >= remaining) complete(next);
  };
  const sendToClover = async () => {
    if (!amount || amount <= 0) return;
    if (amount > remaining) return toast(`That's more than the ${formatCents(remaining)} left to pay.`, 'error');
    setCloverWait('sending');
    try {
      const p = await post('/api/clover/pay', { amountCents: amount, note: `STORE OS sale` });
      addCloverPayment(p);
    } catch (e) {
      // Clover took too long to answer: the customer may still have paid. Ask Clover for a minute.
      if (isApiError(e) && e.code === 'clover_timeout' && e.details?.external_id) {
        setCloverWait('checking');
        const externalId = String(e.details.external_id);
        for (let i = 0; i < 20; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const st = await get(`/api/clover/payments/${encodeURIComponent(externalId)}`);
            if (st.status === 'approved' && st.payment) { addCloverPayment(st.payment); return; }
            if (st.status !== 'started') { toast(st.error || 'The card wasn’t charged. Try again.', 'error'); return; }
          } catch {
            // keep checking
          }
        }
        toast('Still no answer from Clover. Check the Mini: if it shows approved, record the card by hand below.', 'error');
        setByHand(true);
        return;
      }
      toast(e instanceof Error ? e.message : 'The Clover didn’t take the payment.', 'error');
    } finally {
      setCloverWait(null);
    }
  };
  const cancelClover = async () => {
    try {
      await post('/api/clover/cancel');
      toast('Canceled on the Clover.', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not cancel on the Clover.', 'error');
    }
  };

  const addPayment = () => {
    if (!method || !amount || amount <= 0) return;
    if (amount > remaining) return toast(`That's more than the ${formatCents(remaining)} left to pay.`, 'error');
    if (method === 'cash' && tendered !== null && tendered < amount) return toast('Cash given is less than the amount.', 'error');
    if (method === 'card' && last4 && !/^\d{4}$/.test(last4)) return toast('Card last 4 must be 4 digits.', 'error');
    const p: Pay = { method, amountCents: amount, ...(method === 'cash' ? { tenderedCents: tendered ?? amount } : {}), ...(method === 'card' ? { cardLast4: last4 || undefined, externalRef: ref || undefined } : {}) };
    const next = [...pays, p];
    setPays(next);
    setMethod(null);
    if (amount === remaining) complete(next);
  };

  const cashQuick = useMemo(() => {
    const a = amount ?? remaining;
    const opts = new Set<number>([a]);
    for (const step of [500, 1000, 2000, 5000, 10000]) {
      const up = Math.ceil(a / step) * step;
      if (up > a) opts.add(up);
    }
    return [...opts].sort((x, y) => x - y).slice(0, 5);
  }, [amount, remaining]);

  // Payments taken here live only in this window until the sale is saved: a stray tap outside
  // mustn't lose them (and invite running the card twice). Closing then asks first. (Read through a
  // ref: the window's Escape key keeps the close it was first given.)
  const recorded = pays.length > 0;
  const recordedRef = useRef(recorded);
  recordedRef.current = recorded;
  const close = () => (recordedRef.current ? setDropAsk(true) : onClose());

  return (
    <Sheet title="Payment" onClose={close} wide dismissOnBackdrop={!recorded && !busy}>
      {error ? <div className="banner warn"><Icon.alert /><div>{error}</div></div> : null}
      {!quote && !error ? <Spinner label="Pricing the cart" /> : null}
      {quote ? (
        <>
          <div className="stat-row">
            <div className="stat"><div className="k">Total</div><div className="v">{formatCents(total)}</div></div>
            <div className="stat"><div className="k">Paid</div><div className="v">{formatCents(paid)}</div></div>
            <div className="stat" style={{ background: remaining ? 'var(--blue-soft)' : 'var(--green-soft)' }}><div className="k">Left to pay</div><div className="v">{formatCents(remaining)}</div></div>
          </div>
          {quote.tax_removed_cents ? <div className="banner"><Icon.tag /><div className="small">Taxes removed: {formatCents(quote.tax_removed_cents)} ({quote.tax_removed_reason}).</div></div> : null}
          {quote.needs_approval?.length ? <div className="banner warn"><Icon.lock /><div className="small">This sale needs a manager PIN (a price change, a big discount, lots of points or removing the tax). You'll be asked when you finish, so have a manager ready before you run a card.</div></div> : null}
          {quote.points ? (
            <div className="points-box">
              <Icon.star />
              <div className="grow">
                {redeem ? (
                  <div className="strong">{redeem.points.toLocaleString('en-US')} points used · −{formatCents(quote.points.redeem_discount_cents)}</div>
                ) : (
                  <div className="strong">{quote.points.balance.toLocaleString('en-US')} points{quote.points.balance > 0 ? ` (worth ${formatCents(quote.points.balance * quote.points.cents_per_point)})` : ''}</div>
                )}
                <div className="small muted">
                  {quote.points.earn_points ? `Earns ${quote.points.earn_points.toLocaleString('en-US')} points on this sale.` : 'This sale doesn’t earn points.'}
                  {!redeem && quote.points.balance > 0 && quote.points.max_redeem_points < quote.points.min_redeem ? ` Points can be used ${quote.points.min_redeem} or more at a time.` : ''}
                </div>
              </div>
              {redeem ? (
                <Button size="sm" variant="ghost" disabled={pays.length > 0} onClick={removePoints}>Don’t use</Button>
              ) : quote.points.max_redeem_points >= quote.points.min_redeem && !pays.length && !cart.cart.lines.some((l) => l.kind === 'billpay') ? (
                <Button size="sm" variant="primary" onClick={() => setUsingPoints(true)}>Use points</Button>
              ) : null}
            </div>
          ) : null}
          {texting ? (
            texting.opted_out ? (
              <div className="banner warn"><Icon.alert /><div className="small">{buyer?.name} replied STOP to our texts. To get texts again they text START to the store’s number.</div></div>
            ) : (
              <label className="consent-box">
                <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} aria-label="Customer agrees to texts" />
                <span className="small"><strong>Ask: “Can we text you about your repairs, orders and receipts?”</strong><br /><span className="muted">{texting.text}</span></span>
              </label>
            )
          ) : null}
          {usingPoints && quote.points && cart.cart.customer ? (
            <UsePointsPanel points={quote.points} customer={cart.cart.customer} onCancel={() => setUsingPoints(false)} onApply={applyPoints} />
          ) : null}
          {pays.length || autoCredit > 0 ? (
            <div className="list">
              {autoCredit > 0 ? (
                <div className="list-row" style={{ cursor: 'default' }}>
                  <span className="main-col"><span className="t" style={{ display: 'block' }}>Trade-in / store credit</span><span className="s" style={{ display: 'block' }}>Taken off the phone automatically</span></span>
                  <Money cents={autoCredit} className="strong" />
                  <Button size="sm" variant="ghost" onClick={() => setSkipCredit(true)}>Don’t use</Button>
                </div>
              ) : null}
              {pays.map((p, i) => (
                <div key={i} className="list-row" style={{ cursor: 'default' }}>
                  <span className="main-col"><span className="t" style={{ display: 'block' }}>{p.method === 'cash' ? 'Cash' : p.method === 'card' ? `Card${p.cardLast4 ? ` •••• ${p.cardLast4}` : ''}` : 'Store credit'}</span>
                    {p.method === 'cash' && p.tenderedCents && p.tenderedCents > p.amountCents ? <span className="s" style={{ display: 'block' }}>Change due {formatCents(p.tenderedCents - p.amountCents)}</span> : null}
                  </span>
                  <Money cents={p.amountCents} className="strong" />
                  <IconButton icon="close" label="Remove payment" onClick={() => setPays(pays.filter((_, j) => j !== i))} />
                </div>
              ))}
            </div>
          ) : null}
          {remaining > 0 && !method ? (
            <div className="grid-3">
              <Button size="xl" variant="primary" icon="cash" onClick={() => choose('cash')}>Cash</Button>
              <Button size="xl" variant="primary" icon="tag" onClick={() => choose('card')}>Card</Button>
              <Button size="xl" icon="user" disabled={creditLeft <= 0} onClick={() => choose('store_credit')}>
                {creditLeft > 0 ? `Credit ${formatCents(creditLeft)}` : 'Store credit'}
              </Button>
            </div>
          ) : null}
          {method ? (
            <div className="card stack">
              <div className="spread"><h3>{method === 'cash' ? 'Cash' : method === 'card' ? `Card on ${reader.replace('your ', 'the ')}` : 'Store credit'}</h3><Button size="sm" variant="ghost" disabled={!!cloverWait} onClick={() => setMethod(null)}>Change</Button></div>
              <Field label="Amount" hint={amount !== null && amount < remaining ? 'Part of the total. You can add another payment after.' : undefined}>
                <MoneyInput value={amount} onChange={setAmount} />
              </Field>
              {method === 'cash' ? (
                <>
                  <div className="small strong">Cash given</div>
                  <div className="row-wrap">
                    {cashQuick.map((v) => (
                      <button key={v} type="button" className="chip" aria-pressed={tendered === v} onClick={() => setTendered(v)} style={{ minHeight: 52, fontSize: 18 }}>{v === (amount ?? remaining) ? 'Exact' : formatCents(v)}</button>
                    ))}
                  </div>
                  <Field label="Other amount given"><MoneyInput value={tendered} onChange={setTendered} /></Field>
                  {tendered !== null && amount !== null && tendered >= amount ? (
                    <div className="stat" style={{ background: 'var(--green-soft)' }}><div className="k">Change to give back</div><div className="v">{formatCents(tendered - amount)}</div></div>
                  ) : null}
                </>
              ) : null}
              {method === 'card' && cloverReady && !byHand ? (
                cloverWait ? (
                  <div className="card stack-sm" style={{ background: 'var(--blue-soft)' }}>
                    <Spinner label={cloverWait === 'checking' ? 'Clover is slow to answer. Checking whether the card went through…' : `${formatCents(amount ?? 0)} is on the Clover. Waiting for the customer to tap or insert their card…`} />
                    {cloverWait === 'sending' ? <Button variant="ghost" onClick={cancelClover}>Cancel on the Clover</Button> : null}
                  </div>
                ) : (
                  <>
                    <div className="banner"><Icon.info /><div>Tap the button: <strong>{formatCents(amount ?? 0)}</strong> shows on the Clover Mini for the customer to tap or insert their card. The approval comes back here by itself.</div></div>
                    <Button variant="primary" size="lg" block onClick={sendToClover} disabled={!amount || amount <= 0 || busy}>Send {formatCents(amount ?? 0)} to the Clover</Button>
                    <button type="button" className="link-btn small" onClick={() => setByHand(true)}>The card was already run on the Clover: record it by hand</button>
                  </>
                )
              ) : null}
              {method === 'card' && (!cloverReady || byHand) ? (
                <>
                  <div className="banner"><Icon.info /><div>Run <strong>{formatCents(amount ?? 0)}</strong> on {reader}. When it's approved, tap the button below.{cloverReady ? <> <button type="button" className="link-btn" onClick={() => setByHand(false)}>Send it from here instead</button></> : null}</div></div>
                  <div className="form-grid">
                    <Field label="Card last 4 (optional)"><input className="input" inputMode="numeric" maxLength={4} value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, ''))} /></Field>
                    <Field label={reader === 'the Clover' ? 'Clover reference (optional)' : 'Card reader reference (optional)'}><input className="input" value={ref} onChange={(e) => setRef(e.target.value)} /></Field>
                  </div>
                </>
              ) : null}
              {method !== 'card' || !cloverReady || byHand ? (
                <Button variant="primary" size="lg" block busy={busy} onClick={addPayment} disabled={!amount || amount <= 0}>
                  {method === 'card' ? `Approved on ${reader.replace('your ', 'the ')}` : amount === remaining ? 'Take payment and finish' : 'Add payment'}
                </Button>
              ) : null}
            </div>
          ) : null}
          {remaining === 0 && (pays.length > 0 || autoCredit > 0) && !busy ? <Button variant="primary" size="xl" block onClick={() => complete(pays)}>Finish sale</Button> : null}
          {total === 0 && !pays.length ? <Button variant="primary" size="xl" block busy={busy} onClick={() => complete([])}>Finish $0.00 sale</Button> : null}
        </>
      ) : null}
      {dropAsk ? createPortal(
        <Sheet title="Drop the payments recorded so far?" onClose={() => setDropAsk(false)} footer={<><Button variant="primary" onClick={() => setDropAsk(false)}>Keep paying</Button><Button variant="danger" onClick={() => { setDropAsk(false); onClose(); }}>Drop payments</Button></>}>
          <div className="list">
            {pays.map((p, i) => (
              <div key={i} className="list-row" style={{ cursor: 'default' }}>
                <span className="main-col"><span className="t">{p.method === 'cash' ? 'Cash' : p.method === 'card' ? `Card${p.cardLast4 ? ` •••• ${p.cardLast4}` : ''}` : 'Store credit'}</span></span>
                <Money cents={p.amountCents} className="strong" />
              </div>
            ))}
          </div>
          {unsure ? <div className="banner warn"><Icon.alert /><div className="small">The last try to save the sale didn’t finish. Tap <strong>Keep paying</strong>, then finish again: if the sale went through, its receipt opens and nothing is charged twice.</div></div> : null}
          <p style={{ margin: 0 }}>These aren’t saved until the sale is. If you close, void any card payment on {reader}{reader === 'the Clover' ? ' (Clover dashboard → Transactions)' : ''} and give back any cash taken.</p>
        </Sheet>,
        document.body,
      ) : null}
    </Sheet>
  );
}

/**
 * A one-off item that isn’t in inventory (a part ordered for someone, a small service): what it
 * is, the price and whether it’s taxed. It isn’t saved as a product. Staff who can’t change
 * prices get a manager’s PIN at checkout.
 */
export function CustomItemSheet({ name = '', onAdd, onClose }: { name?: string; onAdd: (c: { description: string; priceCents: number; taxable: boolean }) => void; onClose: () => void }) {
  const { can } = useSession();
  const [description, setDescription] = useState(name.trim().replace(/^./, (c) => c.toUpperCase()));
  const [price, setPrice] = useState<number | null>(null);
  const [taxable, setTaxable] = useState(true);
  const ok = description.trim().length >= 2 && price !== null && price > 0;
  const add = () => { if (ok) onAdd({ description: description.trim(), priceCents: price!, taxable }); };
  return (
    <Sheet title="Custom item" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="plus" disabled={!ok} onClick={add}>{price ? `Add to sale · ${formatCents(price)}` : 'Add to sale'}</Button></>}>
      <p className="small muted" style={{ margin: 0 }}>For something sold once that isn’t in inventory. It’s on the receipt, not saved as a product.</p>
      <Field label="What it is">
        <input className="input input-lg" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={120} placeholder="e.g. Ring holder" autoFocus={!description} aria-label="What it is" />
      </Field>
      <Field label="Price"><MoneyInput value={price} onChange={setPrice} autoFocus={!!description} ariaLabel="Price" /></Field>
      <div className="stack-sm">
        <span className="small strong">Sales tax</span>
        <div className="chips" role="group" aria-label="Sales tax">
          <button type="button" className="chip" aria-pressed={taxable} onClick={() => setTaxable(true)}>Taxed</button>
          <button type="button" className="chip" aria-pressed={!taxable} onClick={() => setTaxable(false)}>No tax (labor or a service)</button>
        </div>
      </div>
      {!can('sale.price_override') ? <div className="banner warn"><Icon.lock /><div className="small">A manager’s PIN is needed when you finish the sale.</div></div> : null}
    </Sheet>
  );
}

/** A parked sale as the server keeps it: the cart as it was, who parked it and when. */
interface ParkedSale { id: string; label: string | null; employee_name: string | null; register_name: string | null; created_at: number; cart: Cart | null }

/** "Park sale": the cart is saved for this store (any tablet here can bring it back for 7 days) and emptied. */
function ParkSheet({ onClose, onParked }: { onClose: () => void; onParked: () => void }) {
  const cart = useCart();
  const toast = useToast();
  const { run, busy } = useAction();
  const [note, setNote] = useState('');
  const park = async () => {
    const p = await run(() => post<ParkedSale>('/api/parked-sales', { cart: cart.cart, note: note.trim() || null }));
    if (!p) return;
    cart.clear();
    toast(`Parked: ${p.label ?? 'the sale'}. Bring it back from Parked on New sale.`, 'good');
    onParked();
  };
  return (
    <Sheet title="Park this sale" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="clock" busy={busy} onClick={park}>Park sale</Button></>}>
      <p style={{ margin: 0 }}>The cart is saved and emptied, so you can ring up the next customer. Any tablet at this store can bring it back from <strong>Parked</strong> on New sale for 7 days.</p>
      <Field label="Note" hint="optional"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={80} placeholder="e.g. Back after lunch" autoFocus /></Field>
      <p className="small muted" style={{ margin: 0 }}>Nothing is held for them: a phone in it can still be sold to someone else meanwhile.</p>
    </Sheet>
  );
}

/** "Parked": sales put aside at this store, newest first. Bringing one back takes it off the list. */
function ParkedSheet({ list, loading, onChanged, onClose }: { list: ParkedSale[]; loading: boolean; onChanged: () => void; onClose: () => void }) {
  const cart = useCart();
  const toast = useToast();
  const { me } = useSession();
  const { run, busy } = useAction();
  const [replacing, setReplacing] = useState<ParkedSale | null>(null);
  const [dropping, setDropping] = useState<ParkedSale | null>(null);
  const rate = me?.location?.tax_rate_ppm ?? 0;
  const bringBack = async (p: ParkedSale) => {
    const got = await run(() => post<ParkedSale>(`/api/parked-sales/${p.id}/resume`));
    setReplacing(null);
    onChanged();
    if (!got?.cart) return;
    cart.replace(got.cart);
    toast(`Back in the cart: ${got.label ?? 'the parked sale'}.`, 'good');
    onClose();
  };
  const drop = async (p: ParkedSale) => {
    const ok = await run(() => del(`/api/parked-sales/${p.id}`), { success: 'Parked sale deleted.' });
    setDropping(null);
    if (ok) onChanged();
  };
  const inCart = cart.cart.lines.length;
  return (
    <Sheet title="Parked sales" onClose={onClose} wide footer={<Button onClick={onClose}>Close</Button>}>
      {!list.length ? (loading ? <Spinner /> : <Empty icon="clock" title="No parked sales at this store." />) : (
        <div className="list">
          {list.map((p) => {
            const lines = p.cart?.lines ?? [];
            const items = lines.reduce((n, l) => n + (l.kind === 'product' || l.kind === 'custom' ? l.qty : 1), 0);
            return (
              <div key={p.id} className="list-row" style={{ cursor: 'default' }}>
                <span className="main-col">
                  <span className="t" style={{ display: 'block' }}>{p.label ?? 'Sale'}</span>
                  <span className="s" style={{ display: 'block' }}>{items} item{items === 1 ? '' : 's'} · {formatCents(parkedTotal(p.cart, rate))}{p.cart?.customer && p.label !== p.cart.customer.name ? ` · ${p.cart.customer.name}` : ''}</span>
                  <span className="s" style={{ display: 'block' }}>Parked by {p.employee_name ?? 'someone'} · {formatDateTime(p.created_at)}{p.register_name ? ` · ${p.register_name}` : ''}</span>
                </span>
                <IconButton icon="trash" label={`Delete parked sale ${p.label ?? ''}`} onClick={() => setDropping(p)} />
                <Button variant="primary" disabled={busy} onClick={() => (inCart ? setReplacing(p) : bringBack(p))}>Bring back</Button>
              </div>
            );
          })}
        </div>
      )}
      {replacing ? createPortal(
        <Confirm title="Replace the cart?" confirmLabel="Replace the cart" busy={busy} onClose={() => setReplacing(null)} onConfirm={() => bringBack(replacing)}>
          <p style={{ margin: 0 }}>The cart has {inCart} item{inCart === 1 ? '' : 's'} now. Bringing back <strong>{replacing.label ?? 'the parked sale'}</strong> takes {inCart === 1 ? 'it' : 'them'} out.</p>
          <p className="small muted" style={{ margin: 0 }}>To keep them, close this and park the sale in the cart first.</p>
        </Confirm>,
        document.body,
      ) : null}
      {dropping ? createPortal(
        <Confirm title="Delete this parked sale?" confirmLabel="Delete" danger busy={busy} onClose={() => setDropping(null)} onConfirm={() => drop(dropping)}>
          <p style={{ margin: 0 }}><strong>{dropping.label ?? 'The sale'}</strong> is taken off the list for every tablet. Nothing was charged for it.</p>
        </Confirm>,
        document.body,
      ) : null}
    </Sheet>
  );
}

/** A parked cart's total as this tablet would ring it up now (the server prices it again at Pay). */
function parkedTotal(c: Cart | null, rate: number): number {
  if (!c?.lines?.length) return 0;
  const t = calculateCart(calcLines(c.lines), c.cartDiscountCents ?? 0, c.taxRemoved ? 0 : rate);
  return t.total + faceValueCents(c.lines);
}

/**
 * "Take deposit" on a repair ticket: any amount up to what’s left of its total, paid through the
 * normal checkout. At pickup it comes off what’s due.
 */
export function DepositSheet({ repair, onClose }: { repair: any; onClose: () => void }) {
  const cart = useCart();
  const { me } = useSession();
  const max: number = repair.deposits?.max_cents ?? 0;
  const paid: number = repair.deposits?.open_cents ?? 0;
  const [amount, setAmount] = useState<number | null>(null);
  const tooMuch = amount !== null && amount > max;
  const ok = amount !== null && amount > 0 && !tooMuch;
  const add = async () => {
    if (!ok) return;
    cart.addDeposit({ repairId: repair.id, ticket: repair.ticket, device: repair.device_name ?? `${repair.brand} ${repair.model}`, amountCents: amount! });
    if (!cart.cart.customer && repair.customer?.id) {
      const c = await get(`/api/customers/${repair.customer.id}`).catch(() => null);
      if (c) cart.setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
    }
    onClose();
    navigate('/sale');
  };
  const half = Math.round(max / 2);
  return (
    <Sheet title={`Deposit on ${repair.ticket}`} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="bag" disabled={!ok} onClick={add}>{ok ? `Take ${formatCents(amount!)} deposit` : 'Take deposit'}</Button></>}>
      {!me?.register ? <div className="banner warn"><Icon.alert /><div>Taking a deposit needs a store tablet: it’s paid like a sale.</div></div> : null}
      <p style={{ margin: 0 }}>Any amount up to <strong>{formatCents(max)}</strong>{paid ? ` (${formatCents(paid)} was already paid)` : ' (the ticket total)'}. It’s paid at checkout like a sale, and comes off what’s due at pickup.</p>
      <Field label="Deposit amount"><MoneyInput value={amount} onChange={setAmount} autoFocus ariaLabel="Deposit amount" /></Field>
      <div className="row-wrap">
        {half > 0 && half < max ? <button type="button" className="chip" onClick={() => setAmount(half)}>Half · {formatCents(half)}</button> : null}
        <button type="button" className="chip" onClick={() => setAmount(max)}>All · {formatCents(max)}</button>
      </div>
      {tooMuch ? <div className="error-text" role="alert">A deposit on {repair.ticket} can be up to {formatCents(max)}.</div> : null}
    </Sheet>
  );
}

function OpenDrawerButton({ onOpened }: { onOpened: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="primary" icon="cash" onClick={() => setOpen(true)}>Open drawer</Button>
      {open ? <OpenDrawerSheet onClose={() => setOpen(false)} onDone={() => { setOpen(false); onOpened(); }} /> : null}
    </>
  );
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
